import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import FormattedMessages from '../../../messages/admin/ComplianceFormattedMessages';

import HelpTooltip from '../../common/HelpTooltip';

import {
  Alert,
  Checkbox,
  Button,
  InputLabel,
  MenuItem,
  TextField,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Grid,
  Link,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';

import useComplianceRequest from '../../../requests/ComplianceRequest';

const revetFields = [
  'company_name',
  'ein',
  'ein_issuing_country',
  'entity_type',
];

const BrandDetails = ({
  brand,
  options,
  errorHandler,
  fetchBrand,
  navigate,
}) => {
  const [edits, setEdits] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState({ error: false, errorMessage: '' });
  const [revetModelOpen, setRevetModelOpen] = useState(false);

  const { updateBrand, revetBrand } = useComplianceRequest();

  const { id } = useParams();

  const handleFormChange = (event) => {
    let target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    let name = target.name;

    // console.log('edits', edits);
    setEdits({
      ...edits,
      [name]: value,
    });
  };

  const validateForm = () => {
    let needsRevet = false;
    let error = false;
    let errorMessage = '';
    const revetKeysSet = new Set(revetFields);
    const editKeys = Object.keys(edits);

    const requiredFields = [
      'display_name',
      'company_name',
      'ein',
      'phone',
      'street',
      'city',
      'state',
      'postal_code',
      'country',
      'email',
      'brand_relationship',
      'vertical',
      'reference_id',
    ];

    if (
      edits.entity_type === 'SOLE_PROPRIETOR' ||
      brand.entity_type === 'SOLE_PROPRIETOR'
    ) {
      requiredFields.push('first_name', 'last_name');
    }

    // Check if any required fields are empty in the edits
    for (const field of requiredFields) {
      // Only validate fields that are being edited
      if (field in edits) {
        const value = edits[field];
        if (value === '' || value === null || value === undefined) {
          error = true;
          errorMessage = `Required field ${field.replace(
            /_/g,
            ' '
          )} cannot be empty for entity type ${
            edits.entity_type || brand.entity_type
          }`;
          return { needsRevet, error, errorMessage };
        }
      } else {
        const value = brand[field];
        if (value === '' || value === null || value === undefined) {
          error = true;
          errorMessage = `Required field ${field.replace(
            /_/g,
            ' '
          )} cannot be empty for entity type ${
            edits.entity_type || brand.entity_type
          }`;
          return { needsRevet, error, errorMessage };
        }
      }
    }

    for (let i = 0; i < editKeys.length; i++) {
      const key = editKeys[i];
      if (revetKeysSet.has(key)) {
        if (edits[key] === brand[key]) {
          delete edits[key];
        } else {
          needsRevet = true;
        }
      }
    }
    return { needsRevet, error, errorMessage };
  };

  const handleUpdateBrand = async (modalAccepted) => {
    try {
      setIsSaving(true);
      const { needsRevet, error, errorMessage } = validateForm();

      if (error) {
        setError({ error: error, errorMessage: errorMessage });
        setIsSaving(false);
        return;
      }

      if (!modalAccepted && needsRevet) {
        return setRevetModelOpen(true);
      }

      const updatedBrand = await updateBrand(id, edits, errorHandler);
      console.log('updatedBrand', updatedBrand);
      if (needsRevet) {
        await revetBrand(id, errorHandler);
      }
      setIsSaving(false);
      setEdits({});
      fetchBrand();
    } catch (error) {
      setIsSaving(false);
      errorHandler(error);
    }
  };

  const generateEditFormGroup = (
    label,
    displayValue,
    name,
    required,
    helpToolTipMessage
  ) => {
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          required={required}
          sx={{
            width: 220,
          }}
        >
          {label}
          {helpToolTipMessage ? (
            <HelpTooltip
              message={<Typography>{helpToolTipMessage}</Typography>}
            />
          ) : null}
        </InputLabel>
        <TextField
          key={displayValue}
          name={name}
          defaultValue={displayValue}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
        />
      </Stack>
    );
  };

  const generateAltBusinessIdTypeDropdown = () => {
    const { altBusinessIdType } = options;
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          sx={{
            width: 220,
          }}
        >
          {FormattedMessages.altBusIdType}
        </InputLabel>
        <Select
          labelId="alt-business_id-type-label"
          id="brand-alt-business-id-type"
          name="alt_business_id_type"
          key={brand?.alt_business_id_type}
          defaultValue={brand?.alt_business_id_type || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
          displayEmpty
        >
          {altBusinessIdType?.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const generateBrandRelationshipDropdown = () => {
    const { brandRelationship } = options;
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          required
          sx={{
            width: 220,
          }}
        >
          {FormattedMessages.brandRelationship}
        </InputLabel>
        <Select
          labelId="brand-relationship-label"
          id="brand-relationship"
          name="brand_relationship"
          key={brand?.brand_relationship}
          defaultValue={brand?.brand_relationship || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
          displayEmpty
        >
          {brandRelationship?.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const generateCountryDropdown = (label, name, displayValue, required) => {
    const { country } = options;
    let countryList = [];
    if (country) {
      for (const [key, value] of Object.entries(country)) {
        countryList.push(
          <MenuItem key={key} value={key}>
            {value}
          </MenuItem>
        );
      }
    }

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          required={required}
          sx={{
            width: 220,
          }}
        >
          {label}
        </InputLabel>
        <Select
          labelId="country-label"
          id="country"
          key={displayValue}
          name={name}
          defaultValue={displayValue || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
          displayEmpty
        >
          {countryList}
        </Select>
      </Stack>
    );
  };

  const generateVerticalDropdown = () => {
    console.log('brand?.vertical', brand?.vertical);
    const { vertical } = options;
    let verticalList = [];
    if (vertical) {
      for (const [key, value] of Object.entries(vertical)) {
        verticalList.push(
          <MenuItem key={key} value={key}>
            {value.displayName}
          </MenuItem>
        );
      }
    }
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          required
          sx={{
            width: 220,
          }}
        >
          {FormattedMessages.vertical}
        </InputLabel>
        <Select
          labelId="vertical-label"
          id="vertical"
          name="vertical"
          key={brand?.vertical}
          defaultValue={brand?.vertical || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
          displayEmpty
        >
          {verticalList}
        </Select>
      </Stack>
    );
  };

  const generateEntityTypeDropdown = () => {
    const { entityType } = options;
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          required
          sx={{
            width: 220,
          }}
        >
          {FormattedMessages.entityType}
        </InputLabel>
        <Select
          labelId="entity-type-label"
          id="entity-type"
          name="entity_type"
          key={brand?.entity_type}
          defaultValue={brand?.entity_type || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
          displayEmpty
        >
          {entityType?.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const generateStockExchangeDropdown = () => {
    console.log('stock_exchange', brand.stock_exchange);
    const { stockExchange } = options;
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        marginBottom={2}
      >
        <InputLabel
          sx={{
            width: 220,
          }}
        >
          Stock Exchange:
        </InputLabel>
        <Select
          name="stock_exchange"
          key={brand.stock_exchange}
          value={brand.stock_exchange || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 240 }}
          displayEmpty
        >
          {stockExchange?.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const generateBrandDetails = () => {
    return (
      <Box>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 2 }}>
          {FormattedMessages.companyDetails}
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'Co. Legal Name:',
            brand.company_name,
            'company_name',
            true
          )}
          {generateEditFormGroup(
            'Brand Name or DBA:',
            brand.display_name,
            'display_name',
            true
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEntityTypeDropdown()}
          {generateEditFormGroup('Website:', brand.website, 'website', false)}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup('EIN:', brand.ein, 'ein', true)}
          {generateCountryDropdown(
            'EIN Issuing Country:',
            'ein_issuing_country',
            brand.ein_issuing_country,
            false
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'Alt Business Identifier No.:',
            brand.alt_business_id,
            'alt_business_id',
            false
          )}
          {generateAltBusinessIdTypeDropdown()}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'Address/Street:',
            brand.street,
            'street',
            true
          )}
          {generateEditFormGroup('City:', brand.city, 'city', true)}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'State',
            brand.state,
            'state',
            true,
            `State or province. For the United States, please use 2 character codes. E.g. 'CA' for California.`
          )}
          {generateEditFormGroup(
            'Postal/ZIP Code:',
            brand.postal_code,
            'postal_code',
            true
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateCountryDropdown('Country:', 'country', brand.country, true)}
          {generateVerticalDropdown()}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'Stock Symbol:',
            brand.stock_symbol,
            'stock_symbol',
            false
          )}
          {generateStockExchangeDropdown()}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'First Name:',
            brand.first_name,
            'first_name',
            false
          )}
          {generateEditFormGroup(
            'Last Name:',
            brand.last_name,
            'last_name',
            false
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'Business Contact Email:',
            brand.first_name,
            'first_name',
            false
          )}
          {generateEditFormGroup(
            'Reference ID:',
            brand.reference_id,
            'reference_id',
            true
          )}
        </Stack>
      </Box>
    );
  };

  const generateBrandRelationships = () => {
    return (
      <Box>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 2 }}>
          {FormattedMessages.brandRelationshipTitle}
        </Typography>
        {generateBrandRelationshipDropdown()}
      </Box>
    );
  };

  const generateSupportContactDetails = () => {
    return (
      <Box>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 2 }}>
          {FormattedMessages.contactDetails}
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          columnGap={8}
          marginBottom={2}
        >
          {generateEditFormGroup(
            'Support Contact Email:',
            brand.email,
            'email',
            true
          )}
          {generateEditFormGroup(
            'Support Phone Number:',
            brand.phone,
            'phone',
            true,
            'The support contact telephone in e.164 format. E.g. +12023339999'
          )}
        </Stack>
      </Box>
    );
  };

  const generateCampaignTable = () => {
    const { campaigns } = brand;
    if (!campaigns || campaigns.length === 0) return null;

    const campaignsList = campaigns.map((campaign) => {
      return (
        <TableRow key={campaign.campaign_id}>
          <TableCell>
            <Link
              tabIndex={0}
              onClick={() => {
                navigate('/admin/compliance/campaign/' + campaign.campaign_id);
              }}
            >
              {campaign.campaign_code}
            </Link>
          </TableCell>
          <TableCell>{campaign.campaign_name || 'N/A'}</TableCell>
          <TableCell>{campaign.campaign_status}</TableCell>
          <TableCell>{campaign.customer_name || 'N/A'}</TableCell>
        </TableRow>
      );
    });
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 2 }}>
          {FormattedMessages.campaigns}
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{FormattedMessages.campaignTableCode}</TableCell>
              <TableCell>{FormattedMessages.campaignTableName}</TableCell>
              <TableCell>{FormattedMessages.campaignTableStatus}</TableCell>
              <TableCell>{FormattedMessages.campaignTableCustomer}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{campaignsList}</TableBody>
        </Table>
      </>
    );
  };

  const generateButton = () => {
    return (
      <Stack direction="row" spacing={2} sx={{ marginY: 2 }}>
        <Button variant="link" color="secondary" onClick={() => navigate(-1)}>
          {FormattedMessages.cancel}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleUpdateBrand(false)}
          disabled={isSaving}
        >
          {isSaving ? 'Saving' : 'Save'}
        </Button>
      </Stack>
    );
  };

  const generateRevetModal = () => {
    return (
      <Dialog
        open={revetModelOpen}
        onClose={() => {
          setRevetModelOpen(false);
          setIsSaving(false);
        }}
      >
        <DialogTitle>Revet Brand</DialogTitle>
        <DialogContent>
          <Typography>
            You&apos;ve updated one of the following fields which will
            automatically trigger a revet upon update:
            <ul>
              <li>Company Legal Name</li>
              <li>EIN</li>
              <li>EIN Issuing Country</li>
              <li>Entity Type</li>
            </ul>
            Please confirm to proceed.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setRevetModelOpen(false);
                setIsSaving(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setRevetModelOpen(false);
                handleUpdateBrand(true);
              }}
            >
              Confirm
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box>
      {generateBrandDetails()}
      {generateBrandRelationships()}
      {generateSupportContactDetails()}
      {generateCampaignTable()}
      {generateButton()}
      {generateRevetModal()}
      {error.error && (
        <Grid size={12}>
          <Alert severity="error">{error.errorMessage}</Alert>
        </Grid>
      )}
    </Box>
  );
};

export default BrandDetails;
